import { VoiceInput } from '../components/VoiceInput';
// src/modules/FileVaultModule.tsx
import React, { useState, useEffect, useRef } from 'react';
import { openDB } from 'idb';
import { Upload, Image, File, Trash2, Tag, X, Download } from 'lucide-react';

const DB_NAME = 'RoopCottageVault';
const STORE_NAME = 'files';

const initVaultDB = async () => {
  const db = await openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('tags', 'tags', { multiEntry: true });
        store.createIndex('residentId', 'residentId');
        store.createIndex('incidentId', 'incidentId');
      }
    },
  });
  return db;
};

interface VaultFile {
  id?: number;
  name: string;
  type: string;
  size: number;
  data: Blob;
  uploadedAt: string;
  tags: string[];
  residentId?: string;
  incidentId?: string;
}

export function FileVaultModule({ user }: { user: any }) {
  const [files, setFiles] = useState<VaultFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [tagInput, setTagInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadFiles = async () => {
    const db = await initVaultDB();
    const all = await db.getAll(STORE_NAME);
    setFiles(all);
    setLoading(false);
  };

  useEffect(() => {
    loadFiles();
  }, []);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) return;
    const db = await initVaultDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const filesArray = Array.from(fileList);
    for (const file of filesArray) {
      const blob = new Blob([await file.arrayBuffer()], { type: file.type });
      await store.add({
        name: file.name,
        type: file.type,
        size: file.size,
        data: blob,
        uploadedAt: new Date().toISOString(),
        tags: [],
      });
    }
    await tx.done;
    await loadFiles();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this file?')) return;
    const db = await initVaultDB();
    await db.delete(STORE_NAME, id);
    await loadFiles();
  };

  const handleAddTag = async (id: number, tag: string) => {
    if (!tag.trim()) return;
    const db = await initVaultDB();
    const file = await db.get(STORE_NAME, id);
    if (!file) return;
    file.tags = file.tags || [];
    if (!file.tags.includes(tag.trim())) {
      file.tags.push(tag.trim());
      await db.put(STORE_NAME, file);
      await loadFiles();
    }
    setTagInput('');
  };

  const handleRemoveTag = async (id: number, tag: string) => {
    const db = await initVaultDB();
    const file = await db.get(STORE_NAME, id);
    if (!file) return;
    file.tags = (file.tags || []).filter((t: string) => t !== tag);
    await db.put(STORE_NAME, file);
    await loadFiles();
  };

  const getFileUrl = (file: VaultFile) => {
    return URL.createObjectURL(file.data);
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 flex flex-wrap justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-[#123B36] flex items-center gap-2">
            <Image className="w-6 h-6" /> Secure File Vault
          </h2>
          <p className="text-[#6B7A76]">Central repository for evidence, photos, and documents</p>
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="bg-[#123B36] text-white px-4 py-2 rounded-lg hover:bg-[#0E2C28] flex items-center gap-2 text-sm"
        >
          <Upload className="w-4 h-4" /> Upload Files
        </button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleUpload}
          multiple
          className="hidden"
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        />
      </div>

      {loading ? (
        <div className="text-center p-8">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {files.length === 0 ? (
            <div className="col-span-full text-center text-gray-400 p-8">No files uploaded yet.</div>
          ) : (
            files.map((file) => {
              const isImage = file.type.startsWith('image/');
              const url = getFileUrl(file);
              return (
                <div key={file.id} className="bg-white rounded-lg border border-[#E1DCCD] overflow-hidden shadow-sm hover:shadow-md transition">
                  <div className="aspect-square bg-gray-100 flex items-center justify-center relative">
                    {isImage ? (
                      <img src={url} alt={file.name} className="w-full h-full object-cover" />
                    ) : (
                      <File className="w-12 h-12 text-gray-400" />
                    )}
                    <button
                      onClick={() => handleDelete(file.id!)}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-medium truncate" title={file.name}>{file.name}</p>
                    <p className="text-xs text-gray-500">{formatSize(file.size)}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(file.tags || []).map((tag) => (
                        <span key={tag} className="bg-[#F7F5EE] text-xs px-2 py-0.5 rounded flex items-center gap-1">
                          #{tag}
                          <button onClick={() => handleRemoveTag(file.id!, tag)} className="text-gray-400 hover:text-red-500">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <VoiceInput
                        type="text"
                        placeholder="Add tag..."
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        className="text-xs border rounded px-2 py-1 flex-1"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleAddTag(file.id!, tagInput);
                          }
                        }}
                      />
                      <button
                        onClick={() => handleAddTag(file.id!, tagInput)}
                        className="text-[#123B36] text-xs"
                      >
                        <Tag className="w-4 h-4" />
                      </button>
                      <a href={url} download={file.name} className="text-[#123B36] text-xs">
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}